import { useEffect, useState } from "react"
import { useNavigate, useLocation } from "react-router-dom"
import styled from "styled-components"

import SupportTicketService from "~services/SupportTicketService"
import {
  TicketShape,
  TicketStatus,
  TicketCategory,
  TICKET_STATUS_LABELS,
  TICKET_CATEGORY_LABELS,
} from "~shapes/support-ticket"
import { Table, TableContainer, Th, Tr, Td, NoTableData } from "~styled/table"
import PATHS, { getFullPath } from "~constants/paths"
import colors from "~constants/colors"

const STATUS_CONFIG: Record<
  TicketStatus,
  { background: string; color: string }
> = {
  NEW: { background: "#EEF2FF", color: "#5E81F4" },
  AUTOMATION_QUEUE: { background: "#F3E8FF", color: "#7C3AED" },
  OPEN: { background: "#ECFDF5", color: "#16A34A" },
  WAITING_FOR_USER: { background: "#FFF7ED", color: "#EA580C" },
  RESOLVED: { background: "#F3F4F6", color: "#6B7280" },
  CLOSED: { background: "#FFF1F2", color: "#F54B5E" },
}

interface Props {
  userId: string
  highlightedTicketId?: string
}

const UserTicketsTab = ({ userId, highlightedTicketId }: Props) => {
  const navigate = useNavigate()
  const location = useLocation()
  const [tickets, setTickets] = useState<TicketShape[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (!userId) return
    let cancelled = false
    setIsLoading(true)
    setError(null)
    SupportTicketService.getTickets({
      user_id: userId,
      page_size: 50,
      order_by: "updated_at",
      order_dir: "desc",
    })
      .then(res => {
        if (cancelled) return
        setTickets(res.items)
      })
      .catch(() => {
        if (cancelled) return
        setError("Failed to load tickets.")
      })
      .finally(() => {
        if (cancelled) return
        setIsLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [userId])

  const handleRowClick = (ticketId: string) => {
    sessionStorage.setItem(
      "supportTicketsBack",
      `${location.pathname}${location.search}`,
    )
    navigate(`${getFullPath(PATHS.supportTickets)}/${ticketId}`)
  }

  if (isLoading) {
    return <LoadingText>Loading tickets…</LoadingText>
  }

  if (error) {
    return <ErrorText>{error}</ErrorText>
  }

  return (
    <TableContainer>
      <Table>
        <colgroup>
          <col style={{ width: "32%" }} />
          <col style={{ width: "16%" }} />
          <col style={{ width: "26%" }} />
          <col style={{ width: "13%" }} />
          <col style={{ width: "13%" }} />
        </colgroup>
        <thead>
          <tr>
            <Th style={{ textAlign: "left" }}>Title</Th>
            <Th style={{ textAlign: "left" }}>Status</Th>
            <Th style={{ textAlign: "left" }}>Category</Th>
            <Th style={{ textAlign: "left" }}>Created</Th>
            <Th style={{ textAlign: "left" }}>Updated</Th>
          </tr>
        </thead>
        <tbody>
          {tickets.length === 0 ? (
            <tr>
              <td colSpan={5}>
                <NoTableData>No tickets for this user.</NoTableData>
              </td>
            </tr>
          ) : (
            tickets.map(ticket => {
              const isHighlighted = ticket.id === highlightedTicketId
              return (
                <Tr
                  key={ticket.id}
                  isClickable
                  onClick={() => handleRowClick(ticket.id)}
                  style={
                    isHighlighted
                      ? {
                          background: "#EEF2FF",
                          boxShadow: `inset 3px 0 0 ${colors.blue1}`,
                        }
                      : undefined
                  }
                >
                  <Td>
                    <div style={{ fontWeight: 500 }}>
                      {TICKET_CATEGORY_LABELS[ticket.title as TicketCategory] ??
                        ticket.title}
                    </div>
                  </Td>
                  <Td>
                    <StatusBadge $status={ticket.status}>
                      {TICKET_STATUS_LABELS[ticket.status]}
                    </StatusBadge>
                  </Td>
                  <Td>
                    <div style={{ color: colors.gray3, fontSize: 13 }}>
                      {ticket.category
                        ? (TICKET_CATEGORY_LABELS[
                            ticket.category as TicketCategory
                          ] ?? ticket.category)
                        : "—"}
                    </div>
                  </Td>
                  <Td>
                    <div style={{ color: colors.gray3, fontSize: 13 }}>
                      {new Date(ticket.created_at).toLocaleDateString()}
                    </div>
                  </Td>
                  <Td>
                    <div style={{ color: colors.gray3, fontSize: 13 }}>
                      {new Date(ticket.updated_at).toLocaleDateString()}
                    </div>
                  </Td>
                </Tr>
              )
            })
          )}
        </tbody>
      </Table>
    </TableContainer>
  )
}

export default UserTicketsTab

const StatusBadge = styled.span<{ $status: TicketStatus }>`
  background: ${({ $status }) => STATUS_CONFIG[$status].background};
  color: ${({ $status }) => STATUS_CONFIG[$status].color};
  border-radius: 20px;
  display: inline-block;
  font-size: 12px;
  font-weight: 500;
  line-height: 1;
  padding: 5px 10px;
  white-space: nowrap;
`

const LoadingText = styled.p`
  font-size: 13px;
  color: ${colors.gray6};
  margin: 0;
  padding: 12px 4px;
`

const ErrorText = styled.p`
  font-size: 13px;
  color: ${colors.red};
  margin: 0;
  padding: 12px 4px;
`
